import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { TabsModule } from '../../../tabs/tabs.module';
import { TabItemLike } from '../../../tabs/tabs.component';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from 'app/shared/services/translation.service';
import { Subject, takeUntil } from 'rxjs';
import { BlockModalDragModule } from 'app/shared/directives/block-modal-drag/block-modal-drag.module';
import { AppConfigService } from 'app/shared/services/app-config.service';
import { DeferRenderingModule } from 'app/shared/directives/defer-rendering/defer-rendering.module';
import { IconicModule } from '../../../iconic/iconic.module';
import {
  AvailableCoverageService,
  FixeNetworkResultItemLike,
  MobileNetworkResultItemLike,
  SatelliteNetworkResultItemLike,
} from '../../available-coverage.service';
import {
  ColumnItemLike,
  ResultsTableViewComponent,
} from './components/results-table-view/results-table-view.component';

@Component({
  selector: 'app-available-coverage',
  templateUrl: 'available-coverage.component.html',
  styleUrls: ['available-coverage.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TabsModule,
    IconicModule,
    BlockModalDragModule,
    DeferRenderingModule,
    ResultsTableViewComponent,
  ],
})
export class AvailableCoverageComponent implements OnInit, OnDestroy {
  tabs: TabItemLike[] = [];
  tabIndex = 0;

  fixeNetworkIcon = 'assets/icons/rede-fixa.png';
  get fixeNetworkFooterText(): string {
    return this.resolveFooterHtml(
      this.translationService.getCurrentLanguage()['fixa-msg'],
      this.appConfig.widgetAvailableCoverage?.fixeNetworkLink
    );
  }

  fixeNetworkItems: FixeNetworkResultItemLike[] = [];
  get fixeColumns(): Array<ColumnItemLike> {
    return [
      {
        name: 'operator',
        alias: this.translationService.getCurrentLanguage()['operator'],
        width: '135px',
        minWidth: '70px',
      },
      {
        name: 'technology',
        alias: this.translationService.getCurrentLanguage()['technology'],
        width: '164px',
        minWidth: '80px',
      },
      {
        name: 'downloadSpeed',
        alias: this.translationService.getCurrentLanguage()['download-speed'],
        width: '113px',
        align: 'center',
        minWidth: '112.52px',
      },
      {
        name: 'uploadSpeed',
        alias: this.translationService.getCurrentLanguage()['upload-speed'],
        width: '96.56px',
        minWidth: '96.55px',
        align: 'center',
      },
    ];
  }

  mobileNetworkIcon = 'assets/icons/rede-movel.png';
  get mobileNetworkFooterText(): string {
    return this.resolveFooterHtml(
      this.translationService.getCurrentLanguage()['movel-msg'],
      this.appConfig.widgetAvailableCoverage?.mobileNetworkLink
    );
  }

  mobileNetworkItems: MobileNetworkResultItemLike[] = [];
  get mobileColumns(): Array<ColumnItemLike> {
    return [
      {
        name: 'operator',
        alias: this.translationService.getCurrentLanguage()['operator'],
        width: '178px',
        minWidth: '70px',
      },
      {
        name: 'technology',
        alias: this.translationService.getCurrentLanguage()['technology'],
        width: '152px',
        minWidth: '80px',
      },
      {
        name: 'downloadSpeed',
        alias: this.translationService.getCurrentLanguage()['coverage-download'],
        width: '177.64px',
        align: 'center',
      },
    ];
  }

  satelliteNetworkIcon = 'assets/icons/rede-satelite.png';
  get satelliteNetworkFooterText(): string {
    return this.resolveFooterHtml(
      this.translationService.getCurrentLanguage()['satelite-msg'],
      this.appConfig.widgetAvailableCoverage?.satelliteNetworkLink
    );
  }

  satelliteNetworkItems: SatelliteNetworkResultItemLike[] = [];
  get satelliteColumns(): Array<ColumnItemLike> {
    return [
      {
        name: 'operator',
        alias: this.translationService.getCurrentLanguage()['operator'],
        width: '223px',
        minWidth: '70px',
      },
      {
        name: 'downloadSpeed',
        alias: this.translationService.getCurrentLanguage()['download-speed'],
        width: '200px',
        align: 'center',
        minWidth: '112.52px',
      },
      {
        name: 'uploadSpeed',
        alias: this.translationService.getCurrentLanguage()['upload-speed'],
        width: '100px',
        minWidth: '96.55px',
        align: 'center',
      },
    ];
  }

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private appConfig: AppConfigService,
    private availableCoverageService: AvailableCoverageService,
    private screenService: ScreenService,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    // Inicializar as abas com as traduções
    this.tabs = [
      {
        text: this.translationService.getCurrentLanguage()['fixed-network'],
      },
      {
        text: this.translationService.getCurrentLanguage()['mobile-network'],
      },
      {
        text: this.translationService.getCurrentLanguage()['satellite'],
      },
    ];

    this.availableCoverageService.resultsChanged
      .pipe(takeUntil(this.lifecycle))
      .subscribe((res) => {
        this.fixeNetworkItems = res?.fixeNetworkItems ?? [];
        this.mobileNetworkItems = res?.mobileNetworkItems ?? [];
        this.satelliteNetworkItems = res?.satelliteNetworkItems ?? [];

        this.tabIndex =
          this.fixeNetworkItems.length > 0
            ? 0
            : this.mobileNetworkItems.length > 0
            ? 1
            : this.satelliteNetworkItems.length > 0
            ? 2
            : 0;
      });
  }

  ngOnDestroy() {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  private resolveFooterHtml(text: string, link?: string): string {
    text = text?.replaceAll('\n', '<br />');

    text = text?.replace(
      '{{LINK}}',
      !link
        ? ''
        : `<a href="${this.appConfig.normalizeUrl(link)}" target="_blank">`
    );

    text = text?.replace('{{/LINK}}', !link ? '' : `</a>`);

    return text;
  }
}
